// s4/s5 — Главная (дайджест-триаж, Sprint 6-home-redesign).
// OnboardingRouter маппит оба confirmed state'а (uploadConfirmed1 + …Many) сюда.
//
// Порядок: приветствие → хиро-статус → «Вне нормы» (топ-3 🔴) →
// «Что изменилось» (топ-3) → «Подсказки по анализам» → CTA → «Все показатели →».
// Простыня категорий убрана (живёт в табе «Маркеры»); Wellness перенесён в Профиль.

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { CheckCircle2, ChevronRight, UploadCloud, X } from 'lucide-react';
import { useDashboard } from '@/api/hooks/dashboard';
import { IconTile } from '@/components/ui/icon-tile';
import { saveListScreenState, setMarkerOrigin } from '@/lib/markerNavState';
import { catalogMarkersCount, deriveAttention, markersNavState } from '@/lib/homeAttention';
import { pluralize } from '@/lib/utils';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';
import { BottomNav } from '@/components/layout/BottomNav';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { HomeSkeleton } from '@/components/Skeletons';
import { HomeGreeting } from '@/components/dashboard/HomeGreeting';
import { HeroStatus } from '@/components/dashboard/HeroStatus';
import { AttentionList } from '@/components/dashboard/AttentionList';
import { ChangesList } from '@/components/dashboard/ChangesList';
import { DashboardErrorView } from '@/components/DashboardErrorView';
import { BiomarkerSummaryCard } from '@/components/biomarkers/BiomarkerSummaryCard';
import { UploadSourcePicker } from '@/components/uploads/UploadSourcePicker';
import type { BiomarkerListFilter } from '@/api/types';
import { PendingReviewBarContainer } from '@/components/uploads/PendingReviewBarContainer';
import { CoachMark } from '@/components/onboarding/CoachMark';

const JUST_UPLOADED_KEY = 'healthlab:just_uploaded';

export function HomeConfirmed() {
  const { data, isPending, isError } = useDashboard();
  const [, setLocation] = useLocation();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [bannerInfo, setBannerInfo] = useState<{ count: number; lab: string | null } | null>(null);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem(JUST_UPLOADED_KEY);
    if (raw) {
      try {
        setBannerInfo(JSON.parse(raw) as { count: number; lab: string | null });
      } catch {
        // ignore
      }
      sessionStorage.removeItem(JUST_UPLOADED_KEY);
    }
  }, []);

  // Тап по карточке маркера — снапшот Главной (scroll) + origin для «Назад».
  const openMarker = (markerCode: string) => {
    saveListScreenState('home', { scrollTop: mainRef.current?.scrollTop ?? 0, openByCode: {} });
    setMarkerOrigin('/');
    setLocation(`/markers/${markerCode}`);
  };

  // «Показать все» / «Все маркеры» — уходим в таб «Маркеры» с преднастроенным
  // фильтром (attention → «Вне нормы» на сегменте «По категориям»; changes → без фильтра).
  const goToMarkers = (filter: BiomarkerListFilter, withCategoriesSegment: boolean) => {
    saveListScreenState('markers', markersNavState(filter, withCategoriesSegment));
    setLocation('/markers');
  };

  if (isPending) {
    return (
      <MobileFrame>
        <ScreenHeader title="Главная" right={<NotificationsBell />} />
        <HomeSkeleton />
        <BottomNav active="home" />
      </MobileFrame>
    );
  }
  if (isError || !data) return <DashboardErrorView />;

  const attention = deriveAttention(data.biomarkers_by_category);

  return (
    <MobileFrame>
      <ScreenHeader title="Главная" right={<NotificationsBell />} />

      <main ref={mainRef} className="flex flex-1 flex-col gap-4 overflow-y-auto pb-4">
        {/* HEAL-46a D-18 — единственное, что связывает человека с незаконченным
            разбором после того, как Главная перестала им подменяться. */}
        {/* empty:hidden — контейнер отдаёт null, когда проверять нечего. Без
            этого пустая обёртка съедала pt-2 плюс слот gap-4 у <main>: сверху
            Главной висел зазор, которому не на что отвечать. */}
        <div className="px-4 pt-2 empty:hidden">
          <PendingReviewBarContainer />
        </div>
        {bannerInfo && (
          <section className="px-4 pt-2">
            <div className="flex items-center gap-3 rounded-surface border border-primary/30 bg-primary/10 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-primary-text" aria-hidden="true" />
              <div className="min-w-0 flex-1 text-sm">
                <div className="font-semibold text-primary-text">
                  Распознали {bannerInfo.count}{' '}
                  {pluralize(bannerInfo.count, ['показатель', 'показателя', 'показателей'])}
                </div>
                {bannerInfo.lab && (
                  <div className="truncate text-xs text-primary/80">{bannerInfo.lab}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setBannerInfo(null)}
                aria-label="Закрыть"
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-primary/70 hover:bg-primary/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}

        <HomeGreeting
          analysesCount={data.analyses_count}
          latestMeasuredAt={data.latest_measured_at}
        />

        {data.summary && (
          <HeroStatus
            summary={data.summary}
            extrasCount={data.extras_count}
            attentionCount={attention.length}
            onUnknownClick={() => setLocation('/markers/extra')}
            onAttentionClick={() => goToMarkers('attention', true)}
          />
        )}

        {/* Уровень 2: подсказка приходит в момент, когда есть что объяснять.
            Условие показа — факт первого показа сводки, а НЕ analyses_count === 1:
            после пакетной загрузки счётчик сразу больше единицы, и подсказка
            не досталась бы никому, кто воспользовался пакетом (T-4). */}
        {/* empty:hidden — CoachMark отдаёт null, когда подсказку уже закрыли или
            весь слой выключен, а это состояние у всех, кроме первого показа.
            Без схлопывания пустая обёртка съедала слот gap-4, и между сводкой
            и входами в каталог висела дыра. Та же мина, что была у строки
            непроверенных. */}
        {data.summary && (
          <div className="px-4 empty:hidden">
            <CoachMark id="summary" title="Ваша первая сводка">
              Мы разложили {catalogMarkersCount(data.summary) + data.extras_count} показателей на
              четыре группы. Начните с красной — это то, что вышло за границы нормы лаборатории.
            </CoachMark>
          </div>
        )}

        {/* HEAL-75 изм. 3: два входа в каталог идут подряд, сразу за списком
            «Вне нормы» — с фильтром и без. Внизу экрана ссылки больше нет:
            их было три с тремя разными названиями, и две вели в одно место. */}
        <section className="px-4">
          <AttentionList
            items={attention}
            onOpenMarker={openMarker}
            onShowAll={() => goToMarkers('out_of_range', true)}
            bare
          />

          {data.summary && (
            <button
              type="button"
              onClick={() => goToMarkers('all', false)}
              className="mt-2 flex w-full items-center justify-center rounded-surface border border-primary bg-surface py-2.5 text-hl-secondary font-semibold text-primary-text"
            >
              Все показатели · {catalogMarkersCount(data.summary)} →
            </button>
          )}

          {/* HEAL-75 изм. 2: «Без оценки» было только цифрой в сводке.
              При нуле строки нет вовсе (D-24) — «распознаны, но оценить
              не смогли» на чистом результате описывает несуществующее. */}
          {data.extras_count > 0 && (
            <button
              type="button"
              onClick={() => setLocation('/markers/extra')}
              className="mt-2 flex w-full items-center gap-2.5 rounded-surface border border-border bg-surface px-3 py-2.5 text-left text-hl-secondary text-muted-foreground"
            >
              <span className="h-2 w-2 flex-shrink-0 rounded-full bg-gray-400" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <b className="font-semibold text-foreground">Без оценки · {data.extras_count}</b>
                {' '}— распознаны, но оценить не смогли
              </span>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
            </button>
          )}
        </section>

        <ChangesList changes={data.headline_changes} onOpen={openMarker} />

        <BiomarkerSummaryCard />

        <section className="px-4">
          <button
            type="button"
            onClick={() => setIsUploadOpen(true)}
            className="flex w-full items-center gap-3 rounded-surface bg-primary p-3 text-left text-primary-foreground transition hover:bg-primary/90"
          >
            <IconTile size="sm" tone="inverse">
              <UploadCloud className="h-5 w-5" aria-hidden="true" />
            </IconTile>
            <div className="min-w-0 flex-1 text-sm font-semibold leading-tight">
              Загрузите ещё анализ — увидите динамику
            </div>
            <ChevronRight className="h-5 w-5 flex-shrink-0 text-white/80" aria-hidden="true" />
          </button>
        </section>

      </main>

      <BottomNav active="home" />

      <BottomSheet
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        ariaLabel="Выбор источника файла"
      >
        <UploadSourcePicker
          onClose={() => setIsUploadOpen(false)}
          onUploaded={(uploadId) => setLocation(`/upload/processing/${uploadId}`)}
          onBatchCreated={(batchId) => setLocation(`/uploads/batch/${batchId}`)}
        />
      </BottomSheet>
    </MobileFrame>
  );
}
